import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { adminCmsAPI } from '../../lib/cmsApi';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  GripVertical,
  Eye,
  EyeOff,
  Image,
  User,
  Mail,
  Linkedin,
} from 'lucide-react';

const CmsTeam = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState([]);
  const [editingMember, setEditingMember] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mediaList, setMediaList] = useState([]);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  const defaultMember = {
    name: '',
    slug: '',
    title: '',
    bio: '',
    expertise_tags: [],
    photo_url: '',
    email: '',
    linkedin: '',
    order: 0,
    is_featured: false,
    status: 'publie',
  };

  useEffect(() => {
    if (user?.role !== 'administrateur' && user?.role !== 'employe') {
      navigate('/portail');
      return;
    }
    fetchTeam();
    fetchMedia();
  }, [user, navigate]);

  const fetchTeam = async () => {
    try {
      const response = await adminCmsAPI.listTeam();
      setTeam(response.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'équipe');
    } finally {
      setLoading(false);
    }
  };

  const fetchMedia = async () => {
    try {
      const response = await adminCmsAPI.listMedia('team');
      setMediaList(response.data || []);
    } catch (error) {
      console.error('Error fetching media:', error);
    }
  };

  const handleSave = async () => {
    if (!editingMember.name || !editingMember.slug || !editingMember.title) {
      toast.error('Le nom, le slug et le titre sont requis');
      return;
    }

    setSaving(true);
    try {
      if (editingMember.id) {
        await adminCmsAPI.updateTeamMember(editingMember.id, editingMember);
        toast.success('Membre mis à jour');
      } else {
        await adminCmsAPI.createTeamMember(editingMember);
        toast.success('Membre créé');
      }
      setShowDialog(false);
      setEditingMember(null);
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce membre?')) return;
    
    try {
      await adminCmsAPI.deleteTeamMember(id);
      toast.success('Membre supprimé');
      fetchTeam();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleStatus = async (member) => {
    try {
      const newStatus = member.status === 'publie' ? 'brouillon' : 'publie';
      await adminCmsAPI.updateTeamMember(member.id, { status: newStatus });
      toast.success(`Membre ${newStatus === 'publie' ? 'publié' : 'mis en brouillon'}`);
      fetchTeam();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTagsChange = (text) => {
    const tags = text.split(',').map(t => t.trim()).filter(t => t);
    setEditingMember({ ...editingMember, expertise_tags: tags });
  };

  const selectImage = (url) => {
    setEditingMember({ ...editingMember, photo_url: url });
    setShowMediaPicker(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#F9B438]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="cms-team">
      {/* Header */}
      <header className="bg-[#1D1D1B] text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/cms">
                <Button variant="ghost" size="icon" className="text-white/70 hover:text-white">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div>
                <h1 className="font-heading text-2xl font-bold">Équipe</h1>
                <p className="text-white/60 text-sm">{team.length} membre(s)</p>
              </div>
            </div>
            <Button 
              onClick={() => {
                setEditingMember({ ...defaultMember, order: team.length });
                setShowDialog(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau membre
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {team.map((member) => (
            <Card key={member.id} className="hover:shadow-md transition-shadow overflow-hidden">
              <div className="aspect-square bg-neutral-100 relative">
                {member.photo_url ? (
                  <img 
                    src={member.photo_url} 
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-20 h-20 text-neutral-300" />
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-1">
                  <Badge variant={member.status === 'publie' ? 'default' : 'secondary'}>
                    {member.status === 'publie' ? 'Publié' : 'Brouillon'}
                  </Badge>
                </div>
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold text-[#1D1D1B] mb-1">{member.name}</h3>
                <p className="text-sm text-[#F9B438] mb-2">{member.title}</p>
                {member.expertise_tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {member.expertise_tags.slice(0, 3).map((tag, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between pt-2 border-t">
                  <div className="flex items-center gap-1">
                    {member.email && (
                      <a href={`mailto:${member.email}`}>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Mail className="w-4 h-4" />
                        </Button>
                      </a>
                    )}
                    {member.linkedin && (
                      <a href={member.linkedin} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Linkedin className="w-4 h-4" />
                        </Button>
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => toggleStatus(member)}
                    >
                      {member.status === 'publie' ? (
                        <Eye className="w-4 h-4 text-green-600" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-neutral-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => {
                        setEditingMember(member);
                        setShowDialog(true);
                      }}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleDelete(member.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {team.length === 0 && (
            <Card className="md:col-span-2 lg:col-span-3">
              <CardContent className="p-12 text-center">
                <User className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                <p className="text-neutral-500 mb-4">Aucun membre de l'équipe pour le moment</p>
                <Button 
                  onClick={() => {
                    setEditingMember({ ...defaultMember });
                    setShowDialog(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter le premier membre
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingMember?.id ? 'Modifier le membre' : 'Nouveau membre'}
            </DialogTitle>
          </DialogHeader>
          
          {editingMember && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Photo</Label>
                <div className="flex gap-4 items-start">
                  <div className="w-24 h-24 bg-neutral-100 rounded-full overflow-hidden flex-shrink-0">
                    {editingMember.photo_url ? (
                      <img 
                        src={editingMember.photo_url} 
                        alt="Aperçu"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-10 h-10 text-neutral-300" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <Input
                      value={editingMember.photo_url || ''}
                      onChange={(e) => setEditingMember({ ...editingMember, photo_url: e.target.value })}
                      placeholder="URL de la photo"
                    />
                    <Button variant="outline" size="sm" onClick={() => setShowMediaPicker(true)}>
                      <Image className="w-4 h-4 mr-2" />
                      Choisir dans la médiathèque
                    </Button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom complet *</Label>
                  <Input
                    value={editingMember.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setEditingMember({
                        ...editingMember,
                        name,
                        slug: editingMember.id ? editingMember.slug : generateSlug(name),
                      });
                    }}
                    placeholder="Jean Dupont"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Slug *</Label>
                  <Input
                    value={editingMember.slug}
                    onChange={(e) => setEditingMember({ ...editingMember, slug: e.target.value })}
                    placeholder="jean-dupont"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Titre / Poste *</Label>
                <Input
                  value={editingMember.title}
                  onChange={(e) => setEditingMember({ ...editingMember, title: e.target.value })}
                  placeholder="Ingénieur hydraulique, M.Sc."
                />
              </div>

              <div className="space-y-2">
                <Label>Biographie</Label>
                <Textarea
                  value={editingMember.bio || ''}
                  onChange={(e) => setEditingMember({ ...editingMember, bio: e.target.value })}
                  placeholder="Parcours professionnel et expertise..."
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label>Domaines d'expertise (séparés par des virgules)</Label>
                <Input
                  value={editingMember.expertise_tags?.join(', ') || ''}
                  onChange={(e) => handleTagsChange(e.target.value)}
                  placeholder="Hydrologie, Modélisation, PCSWMM"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Courriel</Label>
                  <Input
                    type="email"
                    value={editingMember.email || ''}
                    onChange={(e) => setEditingMember({ ...editingMember, email: e.target.value })}
                    placeholder="jean@peyconsultants.ca"
                  />
                </div>
                <div className="space-y-2">
                  <Label>LinkedIn</Label>
                  <Input
                    value={editingMember.linkedin || ''}
                    onChange={(e) => setEditingMember({ ...editingMember, linkedin: e.target.value })}
                    placeholder="https://linkedin.com/in/..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Statut</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    value={editingMember.status}
                    onChange={(e) => setEditingMember({ ...editingMember, status: e.target.value })}
                  >
                    <option value="publie">Publié</option>
                    <option value="brouillon">Brouillon</option>
                  </select>
                </div>
                <div className="flex items-center gap-4 pt-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingMember.is_featured}
                      onChange={(e) => setEditingMember({ ...editingMember, is_featured: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Mettre en vedette</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingMember?.id ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Picker Dialog */}
      <Dialog open={showMediaPicker} onOpenChange={setShowMediaPicker}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Choisir une photo</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-4 py-4 max-h-[60vh] overflow-y-auto">
            {mediaList.filter(m => m.file_type === 'image').map((media) => (
              <div
                key={media.id}
                className="aspect-square bg-neutral-100 rounded overflow-hidden cursor-pointer hover:ring-2 hover:ring-[#F9B438] transition-all"
                onClick={() => selectImage(media.url)}
              >
                <img
                  src={media.url}
                  alt={media.alt_text || media.original_filename}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
            {mediaList.filter(m => m.file_type === 'image').length === 0 && (
              <p className="col-span-4 text-center text-neutral-500 py-8">
                Aucune image disponible. Téléversez d'abord des photos dans la médiathèque.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMediaPicker(false)}>
              Annuler
            </Button>
            <Link to="/cms/media">
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Téléverser une photo
              </Button>
            </Link>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CmsTeam;
